<template>
  <transition name="diagnostics-modal-fade" :css="!inline">
    <div
      v-if="visible"
      :class="
        inline ? 'diagnostics-inline-container' : 'diagnostics-modal-backdrop'
      "
      @click.self="inline ? null : close()"
    >
      <section
        class="diagnostics-modal"
        :class="{ 'diagnostics-modal--inline': inline }"
        :role="inline ? 'region' : 'alertdialog'"
        :aria-modal="inline ? null : 'true'"
        aria-labelledby="conversion-diagnostics-title"
      >
        <header class="diagnostics-modal-header">
          <div class="diagnostics-modal-icon" :class="statusClass">
            <i :class="iconClass"></i>
          </div>
          <div>
            <p v-if="!inline" class="diagnostics-modal-eyebrow">
              {{ eyebrow }}
            </p>
            <h3 id="conversion-diagnostics-title">{{ title }}</h3>
          </div>
          <button
            v-if="!inline"
            class="diagnostics-modal-close"
            type="button"
            title="Chiudi"
            @click="close"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div
          v-if="$slots['context-actions']"
          class="diagnostics-context-actions"
        >
          <slot name="context-actions"></slot>
        </div>

        <div class="diagnostics-modal-body">
          <p class="diagnostics-modal-message">
            {{ userMessage }}
          </p>

          <ul v-if="visibleUserEvents.length" class="diagnostics-event-list">
            <template v-for="(event, index) in visibleUserEvents">
              <li
                v-if="teamSystemGroupHeading(event, index)"
                :key="`${eventKey(event)}-group`"
                class="diagnostics-event-group-row"
              >
                <span class="diagnostics-event-group-heading">
                  {{ teamSystemGroupHeading(event, index) }}
                </span>
              </li>
              <li :key="eventKey(event)">
                <span
                  v-if="hasProspettoPagheKey(event)"
                  class="diagnostics-event-badge"
                >
                  PROSPETTO PAGHE
                </span>
                <div class="diagnostics-event-heading">
                  <strong>{{ eventTitle(event) }}</strong>
                  <span
                    v-if="eventFullPagesLabel(event)"
                    class="diagnostics-event-pages"
                  >
                    {{ eventFullPagesLabel(event) }}
                  </span>
                </div>
                <p v-if="eventMessage(event)" class="diagnostics-event-copy">
                  {{ eventMessage(event) }}
                </p>
                <div
                  v-if="eventDetails(event).length"
                  class="diagnostics-event-details"
                >
                  <p
                    v-for="(detail, detailIndex) in eventDetails(event)"
                    :key="`${eventKey(event)}-detail-${detailIndex}`"
                  >
                    <span
                      v-if="
                        showDetailPages(event) &&
                        detail.pages &&
                        detail.pages.length
                      "
                      class="diagnostics-detail-page-label"
                    >
                      Pag. {{ detail.pages.join(", ") }} —
                    </span>
                    {{ detail.message }}
                  </p>
                </div>
                <template
                  v-if="
                    !eventDetails(event).length && eventCauses(event).length
                  "
                >
                  <ul class="diagnostics-event-cause-list">
                    <li
                      v-for="cause in eventCauses(event)"
                      :key="`${eventKey(event)}-${cause}`"
                    >
                      {{ cause }}
                    </li>
                  </ul>
                </template>
                <template
                  v-if="
                    !eventDetails(event).length && eventSections(event).length
                  "
                >
                  <ul class="diagnostics-event-section-list">
                    <li
                      v-for="section in eventSections(event)"
                      :key="`${eventKey(event)}-${section}`"
                    >
                      {{ section }}
                    </li>
                  </ul>
                </template>
              </li>
            </template>
          </ul>

          <div
            v-if="showProcessedClients"
            class="diagnostics-processed-clients"
          >
            <div class="diagnostics-processed-clients-header">
              <span>
                Questi Prospetti Paghe sono stati invece elaborati
                correttamente:
              </span>
            </div>
            <ul>
              <li v-for="client in processedClients" :key="client">
                {{ client }}
              </li>
            </ul>
          </div>

          <p v-if="supportMessage" class="diagnostics-modal-support">
            {{ supportMessage }}
          </p>
        </div>

        <footer
          class="diagnostics-modal-actions"
          :class="{ 'diagnostics-modal-actions--split': canGoBack }"
        >
          <button
            v-if="canGoBack"
            type="button"
            class="diagnostics-modal-back"
            @click="goBack"
          >
            Indietro
          </button>
          <div class="diagnostics-modal-main-actions">
            <button
              v-if="canReviewSections"
              type="button"
              class="diagnostics-modal-secondary"
              @click="close"
            >
              Continua con sezioni escluse
            </button>
            <button
              type="button"
              @click="canReviewSections ? reviewSections() : close()"
            >
              {{ canReviewSections ? "Rivedi sezioni" : actionLabel }}
              <i
                v-if="inline && !canReviewSections"
                class="fa-solid fa-arrow-right diagnostics-inline-next-icon"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ConversionDiagnosticsModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: Object,
      default: null,
    },
    backendMessage: {
      type: String,
      default: "",
    },
    processedClients: {
      type: Array,
      default: () => [],
    },
    canGoBack: {
      type: Boolean,
      default: false,
    },
    canReviewSections: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    finalActionLabel: {
      type: String,
      default: "",
    },
  },
  computed: {
    status() {
      return this.summary?.status || "failed";
    },
    userEvents() {
      return Array.isArray(this.summary?.user_events)
        ? this.summary.user_events
        : [];
    },
    visibleUserEvents() {
      return this.userEvents;
    },
    statusClass() {
      return `diagnostics-modal-icon--${this.status}`;
    },
    iconClass() {
      if (this.status === "warning") return "fa-solid fa-circle-info";
      return "fa-solid fa-triangle-exclamation";
    },
    eyebrow() {
      return "Controllo qualità documento";
    },
    title() {
      if (this.summary?.user_title) return this.summary.user_title;
      if (this.status === "warning") return "Dati estratti con avvisi";
      if (this.status === "degraded") return "Dati estratti parzialmente";
      return "Estrazione dati non completata";
    },
    userMessage() {
      if (this.summary?.user_message) return this.summary.user_message;
      if (this.status === "warning") {
        return "I dati sono stati estratti, ma potrebbero essere incompleti: alcune parti del documento non sono state lette con piena sicurezza.";
      }
      if (this.status === "degraded") {
        return "I dati sono stati estratti solo in parte: alcune pagine o prospetti sono stati esclusi dall'elaborazione.";
      }
      return "Non siamo riusciti a estrarre i dati necessari per proseguire con l'elaborazione. Controlla i dati qui sotto:";
    },
    supportMessage() {
      if (
        this.summary &&
        Object.prototype.hasOwnProperty.call(this.summary, "support_message")
      ) {
        return this.summary.support_message || "";
      }
      if (this.status === "failed") {
        return "Controlla che il PDF caricato sia corretto e nel formato leggibile da MyStudio. Se il problema persiste, segnalalo allo staff di MyStudio.";
      }
      return "";
    },
    actionLabel() {
      if (this.summary?.has_more_notifications) return "Avanti";
      return this.finalActionLabel || "Ho capito";
    },
    showProcessedClients() {
      return (
        this.status === "degraded" &&
        this.summary?.current_notification_kind !== "teamsystem_review" &&
        this.processedClients.length > 0
      );
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible && !this.inline) {
        document.addEventListener("keydown", this.handleKeydown);
      } else {
        document.removeEventListener("keydown", this.handleKeydown);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    close() {
      this.$emit("close");
    },
    goBack() {
      this.$emit("back");
    },
    reviewSections() {
      this.$emit("review-sections");
    },
    eventKey(event) {
      const pages = Array.isArray(event.pages) ? event.pages.join("-") : "";
      const sample = event.sample || {};
      const prospettoKey = [
        sample.codice_ditta,
        sample.denominazione_cliente,
        sample.month,
        sample.year,
      ]
        .filter(Boolean)
        .join("-");
      return `${event.category || "event"}-${
        event.event_type || "unknown"
      }-${pages}-${prospettoKey}`;
    },
    eventSections(event) {
      return Array.isArray(event.section_labels) ? event.section_labels : [];
    },
    eventCauses(event) {
      return Array.isArray(event.cause_labels) ? event.cause_labels : [];
    },
    eventDetails(event) {
      return Array.isArray(event?.details) ? event.details : [];
    },
    eventMessage(event) {
      return event?.message || "";
    },
    eventTitle(event) {
      if (
        this.summary?.operation_type === "prospetti_paghe" &&
        !this.hasProspettoPagheKey(event) &&
        Array.isArray(event?.pages) &&
        event.pages.length
      ) {
        const pages = Array.isArray(event?.pages) ? event.pages : [];
        return `Pag. ${pages.join(", ")}`;
      }
      return this.cleanEventTitle(event?.title || "Anomalia rilevata");
    },
    showDetailPages(event) {
      return event?.details?.length > 1 && event?.category === "section";
    },
    teamSystemGroupHeading(event, index) {
      if (this.summary?.current_notification_kind !== "teamsystem_review")
        return "";
      const hasKey = this.hasProspettoPagheKey(event);
      if (
        index > 0 &&
        hasKey === this.hasProspettoPagheKey(this.visibleUserEvents[index - 1])
      )
        return "";
      return hasKey ? "PROSPETTI" : "SINGOLE PAGINE";
    },
    hasProspettoPagheKey(event) {
      const sample = event?.sample || {};
      return Boolean(
        sample.denominazione_cliente && (sample.month || sample.year)
      );
    },
    cleanEventTitle(title) {
      const cleanedTitle = String(title || "")
        .replace(/^Prospetto Paghe escluso:\s*/i, "")
        .replace(/^Prospetto Paghe da verificare:\s*/i, "")
        .trim();
      if (/^Impossibile riconoscere pagin[ae]$/i.test(cleanedTitle)) {
        return "Layout Prospetto Paghe non riconosciuto";
      }
      return cleanedTitle;
    },
    eventFullPagesLabel(event) {
      if (
        this.summary?.operation_type === "prospetti_paghe" &&
        !this.hasProspettoPagheKey(event)
      ) {
        return "";
      }
      if (
        this.summary?.current_notification_kind === "f24_warnings" ||
        (this.summary?.current_notification_kind === "excluded_pages" &&
          this.summary?.operation_type === "modelli_f24")
      )
        return "";
      const pages = Array.isArray(event?.pages)
        ? event.pages.filter(
            (page) => page !== null && page !== undefined && page !== ""
          )
        : [];
      if (!pages.length) return "";
      return `[Pag. ${pages.join(", ")}]`;
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.diagnostics-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(18, 24, 32, 0.58);
}

.diagnostics-inline-container {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  animation: diagnostics-inline-enter 0.18s ease both;
}

.diagnostics-modal {
  width: min(50rem, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  color: #252a31;
  text-align: left;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
}

.diagnostics-modal--inline {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  box-shadow: 0 8px 24px rgba(35, 48, 65, 0.08);
}

.diagnostics-modal--inline .diagnostics-modal-body {
  flex: 1 1 0;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.diagnostics-modal--inline .diagnostics-modal-header,
.diagnostics-modal--inline .diagnostics-context-actions,
.diagnostics-modal--inline .diagnostics-modal-actions {
  flex: 0 0 auto;
}

.diagnostics-modal--inline .diagnostics-modal-header {
  gap: 0.7rem;
  padding: 0.75rem 1rem;
}

.diagnostics-modal--inline .diagnostics-modal-icon {
  width: 2rem;
  height: 2rem;
}

.diagnostics-modal--inline h3 {
  font-size: 1.05rem;
  line-height: 1.2;
}

.diagnostics-context-actions {
  min-width: 0;
}

.diagnostics-modal-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.9rem;
  align-items: center;
  padding: 1.15rem 1.25rem;
  background: #f5f7fa;
  border-bottom: 1px solid #e2e5e9;
}

.diagnostics-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.45rem;
  height: 2.45rem;
  border-radius: 50%;
}

.diagnostics-modal-icon--failed {
  color: #9f2f2f;
  background: #fff1f1;
  border: 1px solid #f3c7c7;
}

.diagnostics-modal-icon--degraded {
  color: #8a4b00;
  background: #fff5df;
  border: 1px solid #f2d39b;
}

.diagnostics-modal-icon--warning {
  color: #9a6400;
  background: #fff7df;
  border: 1px solid #f2d08a;
}

.diagnostics-modal-eyebrow {
  margin: 0 0 0.2rem;
  color: #657186;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.diagnostics-modal h3 {
  margin: 0;
  color: #202733;
  font-size: 1.18rem;
  font-weight: 800;
  line-height: 1.25;
}

.diagnostics-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: #69707b;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
}

.diagnostics-modal-close:hover {
  color: #252a31;
  background: #e8eaee;
}

.diagnostics-modal-body {
  min-height: 0;
  overflow-y: auto;
  padding: 1.35rem 1.5rem;
}

.diagnostics-modal-message,
.diagnostics-modal-support {
  margin: 0;
  color: #3f4651;
  font-size: 1rem;
  line-height: 1.5;
}

.diagnostics-modal-support {
  margin-top: 1rem;
  color: #5d6877;
}

.diagnostics-event-list {
  display: grid;
  gap: 0.6rem;
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
}

.diagnostics-event-list > li {
  display: grid;
  gap: 0.32rem;
  padding: 0.55rem 0.7rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
}

.diagnostics-event-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.diagnostics-event-group-heading {
  margin-bottom: 0.2rem;
  color: #496074;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.diagnostics-event-list > li.diagnostics-event-group-row {
  padding: 0.65rem 0 0.05rem;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.diagnostics-event-copy,
.diagnostics-event-details p {
  margin: 0;
  color: #344253;
  font-size: 0.9rem;
  line-height: 1.5;
}

.diagnostics-event-details {
  display: grid;
  gap: 0.55rem;
}

.diagnostics-detail-page-label {
  color: #245984;
  font-weight: 700;
}

.diagnostics-event-list strong {
  color: #202733;
  font-size: 0.92rem;
}

.diagnostics-event-list .diagnostics-event-badge {
  width: max-content;
  margin-left: -0.12rem;
  margin-bottom: 0.08rem;
  padding: 0.16rem 0.45rem;
  color: #49695f;
  background: #eef5f2;
  border: 1px solid #c8d9d2;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.1;
}

.diagnostics-event-list > li > :not(.diagnostics-event-badge) {
  margin-left: 0.25rem;
}

.diagnostics-event-list span {
  color: #4b5565;
  font-size: 0.9rem;
  line-height: 1.45;
}

.diagnostics-event-list .diagnostics-event-pages {
  color: #5d6877;
  font-size: 0.86rem;
}

.diagnostics-processed-clients {
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid #e5e9ef;
}

.diagnostics-processed-clients-header {
  color: #3f4651;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.4;
}

.diagnostics-processed-clients ul {
  max-height: 7.2rem;
  overflow-y: auto;
  margin: 0.65rem 0 0;
  padding: 0.55rem 0.75rem 0.55rem 1.35rem;
  color: #4b5565;
  background: #f7f9fb;
  border: 1px solid #e1e7ef;
  border-radius: 6px;
  font-size: 0.9rem;
  line-height: 1.45;
}

.diagnostics-event-section-list {
  margin: -0.15rem 0 0 1rem;
  padding-left: 1.5rem;
  color: #4b5565;
  font-size: 0.9rem;
  line-height: 1.4;
}

.diagnostics-event-cause-list {
  margin: 0rem 0 0;
  padding-left: 0;
  color: #202733;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
}

.diagnostics-event-section-list li {
  margin: 0.1rem 0;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  list-style: disc;
}

.diagnostics-event-cause-list li {
  display: flex;
  gap: 0.45rem;
  align-items: baseline;
  margin: 0rem 0;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  list-style: none;
}

.diagnostics-event-cause-list li::before {
  content: "•";
  color: #202733;
  font-weight: 900;
}

.diagnostics-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e5e9;
  background: #fafafa;
}

.diagnostics-modal-actions--split {
  justify-content: space-between;
}

.diagnostics-modal-main-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
}

.diagnostics-modal-actions button {
  height: 2.5rem;
  border: 0;
  border-radius: 6px;
  padding: 0 1rem;
  background: #2454a6;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
}

.diagnostics-modal-actions button:hover {
  background: #1d4385;
}

.diagnostics-inline-next-icon {
  margin-left: 0.5rem;
}

.diagnostics-modal-actions .diagnostics-modal-back {
  color: #2454a6;
  background: #ffffff;
  border: 1px solid #b9c9e7;
}

.diagnostics-modal-actions .diagnostics-modal-secondary {
  color: #2454a6;
  background: #ffffff;
  border: 1px solid #b9c9e7;
}

.diagnostics-modal-actions .diagnostics-modal-back:hover,
.diagnostics-modal-actions .diagnostics-modal-secondary:hover {
  background: #f2f6fd;
}

.diagnostics-modal-fade-enter-active,
.diagnostics-modal-fade-leave-active {
  transition: opacity 0.16s ease;
}

.diagnostics-modal-fade-enter,
.diagnostics-modal-fade-leave-to {
  opacity: 0;
}

@keyframes diagnostics-inline-enter {
  from {
    opacity: 0;
    transform: translateX(0.6rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
